/**
 * Crypt Dice Component
 *
 * Renders the crypt-themed dice with an ornate gold/bronze frame,
 * white bone/candle decorations on the border, and a green grass inner face.
 * Pip indicators use bone/candle shapes instead of circular dots.
 *
 * SVG structure extracted from Figma exports (red_dice_3 and yellow_dice_6 references).
 * Reuses useDiceViewModel for shared animation/interaction logic.
 */

import React from 'react';
import { motion } from 'framer-motion';
import type { DiceProps } from '../basics/constants/types';
import type { CryptDiceColorScheme } from './constants/diceColorSchemes';
import { useDiceViewModel } from '../basics/hooks/useDiceViewModel';
import { GRASS_TEXTURE_BASE64, DICE_CONFIG } from '../basics/utils/utils';
import {
  CRYPT_PATHS,
  CRYPT_GRADIENTS,
  CRYPT_DECORATIONS,
  CRYPT_TEXTURE_RECT,
  CRYPT_DICE_FACES,
  FACE_6_SKULL_BODY,
  FACE_6_RIGHT_EYE_SHADOW,
  FACE_6_RIGHT_EYE_MAIN,
  FACE_6_LEFT_EYE_SHADOW,
  FACE_6_LEFT_EYE_MAIN
} from './constants/cryptDiceData';
import '../basics/Dice.css';

interface CryptDiceProps extends DiceProps {
  colorScheme: CryptDiceColorScheme;
}

const SHADOW_OPACITY = 0.55;
const DECORATION_OPACITY = 0.4;
const FACE_VIGNETTE_OPACITY = 0.46;

const CryptDiceComponent = React.memo(function CryptDiceComponent({
  colorScheme,
  ...diceProps
}: CryptDiceProps) {
  const {
    showDiceFace = true,
    showGround = true,
    isRolling = false,
    showGrassTexture = true,
    viewBox = '0 0 187 198'
  } = diceProps;

  const { state, actions } = useDiceViewModel(diceProps);

  const indicators = CRYPT_DICE_FACES[state.displayedValue] ?? [];
  const isValue6 = state.displayedValue === 6;

  const getId = state.getId;
  const g = CRYPT_GRADIENTS;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={viewBox}
      className={state.svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      role="img"
      aria-label={`Dice showing ${String(state.validValue)}`}
      aria-live={isRolling ? 'polite' : 'off'}
      focusable="false"
      onClick={actions.handleClick}
      style={{ cursor: state.cursorStyle }}
    >
      <defs>
        {/* Clip path */}
        <clipPath id={getId('clip')}>
          <rect width="186.67" height="197.33" fill="white" />
        </clipPath>

        {/* Drop shadow filter (for animated face group) */}
        <filter id={getId('dropShadow')} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation={DICE_CONFIG.SHADOW_3D.BLUR_RADIUS} />
          <feOffset
            dx={DICE_CONFIG.SHADOW_3D.OFFSET_X}
            dy={DICE_CONFIG.SHADOW_3D.OFFSET_Y}
            result="offsetblur"
          />
          <feComponentTransfer>
            <feFuncA type="linear" slope={DICE_CONFIG.SHADOW_3D.OPACITY} />
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Grass texture pattern */}
        {showGrassTexture && (
          <pattern
            id={getId('texture')}
            patternContentUnits="objectBoundingBox"
            width="1"
            height="1"
          >
            <use xlinkHref={`#${getId('textureImg')}`} transform="scale(0.00438596)" />
          </pattern>
        )}
        {showGrassTexture && (
          <image
            id={getId('textureImg')}
            width="228"
            height="228"
            preserveAspectRatio="none"
            xlinkHref={GRASS_TEXTURE_BASE64}
          />
        )}

        {/* Body gradient — player-specific color */}
        <linearGradient
          id={getId('body')}
          x1="15.97"
          y1="83.69"
          x2="170.45"
          y2="83.69"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyGradient[0]} />
          <stop offset="0.24" stopColor={colorScheme.bodyGradient[1]} />
          <stop offset="0.47" stopColor={colorScheme.bodyGradient[2]} />
          <stop offset="0.75" stopColor={colorScheme.bodyGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyGradient[4]} />
        </linearGradient>

        {/* Corner left gradient — player-specific */}
        <linearGradient
          id={getId('cornerLeft')}
          x1="3.15282e-05"
          y1="106.51"
          x2="53.41"
          y2="106.51"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyGradient[0]} />
          <stop offset="0.24" stopColor={colorScheme.bodyGradient[1]} />
          <stop offset="0.47" stopColor={colorScheme.bodyGradient[2]} />
          <stop offset="0.75" stopColor={colorScheme.bodyGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyGradient[4]} />
        </linearGradient>

        {/* Corner right gradient — player-specific */}
        <linearGradient
          id={getId('cornerRight')}
          x1="105.29"
          y1="134.84"
          x2="1.00003"
          y2="134.84"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyGradient[0]} />
          <stop offset="0.24" stopColor={colorScheme.bodyGradient[1]} />
          <stop offset="0.47" stopColor={colorScheme.bodyGradient[2]} />
          <stop offset="0.75" stopColor={colorScheme.bodyGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyGradient[4]} />
        </linearGradient>

        {/* Corner top-left gradient — player-specific */}
        <linearGradient
          id={getId('cornerTopLeft')}
          x1="3.00763e-05"
          y1="31.57"
          x2="89.3"
          y2="31.57"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyGradient[0]} />
          <stop offset="0.24" stopColor={colorScheme.bodyGradient[1]} />
          <stop offset="0.47" stopColor={colorScheme.bodyGradient[2]} />
          <stop offset="0.75" stopColor={colorScheme.bodyGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyGradient[4]} />
        </linearGradient>

        {/* Right frame gold gradient */}
        <linearGradient
          id={getId('frameRightGold')}
          x1={g.FRAME_RIGHT_GOLD.x1}
          y1={g.FRAME_RIGHT_GOLD.y1}
          x2={g.FRAME_RIGHT_GOLD.x2}
          y2={g.FRAME_RIGHT_GOLD.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.FRAME_RIGHT_GOLD.start} />
          <stop offset="1" stopColor={g.FRAME_RIGHT_GOLD.end} />
        </linearGradient>

        {/* Right frame dark gradient */}
        <linearGradient
          id={getId('frameRightDark')}
          x1={g.FRAME_RIGHT_DARK.x1}
          y1={g.FRAME_RIGHT_DARK.y1}
          x2={g.FRAME_RIGHT_DARK.x2}
          y2={g.FRAME_RIGHT_DARK.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.FRAME_RIGHT_DARK.start} />
          <stop offset="1" stopColor={g.FRAME_RIGHT_DARK.end} />
        </linearGradient>

        {/* Left frame gold gradient */}
        <linearGradient
          id={getId('frameLeftGold')}
          x1={g.FRAME_LEFT_GOLD.x1}
          y1={g.FRAME_LEFT_GOLD.y1}
          x2={g.FRAME_LEFT_GOLD.x2}
          y2={g.FRAME_LEFT_GOLD.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.FRAME_LEFT_GOLD.start} />
          <stop offset="1" stopColor={g.FRAME_LEFT_GOLD.end} />
        </linearGradient>

        {/* Left frame dark gradient */}
        <linearGradient
          id={getId('frameLeftDark')}
          x1={g.FRAME_LEFT_DARK.x1}
          y1={g.FRAME_LEFT_DARK.y1}
          x2={g.FRAME_LEFT_DARK.x2}
          y2={g.FRAME_LEFT_DARK.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.FRAME_LEFT_DARK.start} />
          <stop offset="1" stopColor={g.FRAME_LEFT_DARK.end} />
        </linearGradient>

        {/* Top frame gold gradient */}
        <linearGradient
          id={getId('frameTopGold')}
          x1={g.FRAME_TOP_GOLD.x1}
          y1={g.FRAME_TOP_GOLD.y1}
          x2={g.FRAME_TOP_GOLD.x2}
          y2={g.FRAME_TOP_GOLD.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.FRAME_TOP_GOLD.start} />
          <stop offset="1" stopColor={g.FRAME_TOP_GOLD.end} />
        </linearGradient>

        {/* Top frame dark gradient */}
        <linearGradient
          id={getId('frameTopDark')}
          x1={g.FRAME_TOP_DARK.x1}
          y1={g.FRAME_TOP_DARK.y1}
          x2={g.FRAME_TOP_DARK.x2}
          y2={g.FRAME_TOP_DARK.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.FRAME_TOP_DARK.start} />
          <stop offset="1" stopColor={g.FRAME_TOP_DARK.end} />
        </linearGradient>

        {/* Bottom frame gold gradient */}
        <linearGradient
          id={getId('frameBottomGold')}
          x1={g.FRAME_BOTTOM_GOLD.x1}
          y1={g.FRAME_BOTTOM_GOLD.y1}
          x2={g.FRAME_BOTTOM_GOLD.x2}
          y2={g.FRAME_BOTTOM_GOLD.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.FRAME_BOTTOM_GOLD.start} />
          <stop offset="1" stopColor={g.FRAME_BOTTOM_GOLD.end} />
        </linearGradient>

        {/* Bottom frame dark gradient */}
        <linearGradient
          id={getId('frameBottomDark')}
          x1={g.FRAME_BOTTOM_DARK.x1}
          y1={g.FRAME_BOTTOM_DARK.y1}
          x2={g.FRAME_BOTTOM_DARK.x2}
          y2={g.FRAME_BOTTOM_DARK.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.FRAME_BOTTOM_DARK.start} />
          <stop offset="1" stopColor={g.FRAME_BOTTOM_DARK.end} />
        </linearGradient>

        {/* Corner gradients (gold) */}
        <linearGradient
          id={getId('cornerTRGrad')}
          x1={g.CORNER_TR.x1}
          y1={g.CORNER_TR.y1}
          x2={g.CORNER_TR.x2}
          y2={g.CORNER_TR.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.CORNER_TR.start} />
          <stop offset="1" stopColor={g.CORNER_TR.end} />
        </linearGradient>
        <linearGradient
          id={getId('cornerTLGrad')}
          x1={g.CORNER_TL.x1}
          y1={g.CORNER_TL.y1}
          x2={g.CORNER_TL.x2}
          y2={g.CORNER_TL.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.CORNER_TL.start} />
          <stop offset="1" stopColor={g.CORNER_TL.end} />
        </linearGradient>
        <linearGradient
          id={getId('cornerBLGrad')}
          x1={g.CORNER_BL.x1}
          y1={g.CORNER_BL.y1}
          x2={g.CORNER_BL.x2}
          y2={g.CORNER_BL.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.CORNER_BL.start} />
          <stop offset="1" stopColor={g.CORNER_BL.end} />
        </linearGradient>
        <linearGradient
          id={getId('cornerBRGrad')}
          x1={g.CORNER_BR.x1}
          y1={g.CORNER_BR.y1}
          x2={g.CORNER_BR.x2}
          y2={g.CORNER_BR.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.CORNER_BR.start} />
          <stop offset="1" stopColor={g.CORNER_BR.end} />
        </linearGradient>

        {/* Inner face grass gradient */}
        <linearGradient
          id={getId('innerFace')}
          x1={g.INNER_FACE.x1}
          y1={g.INNER_FACE.y1}
          x2={g.INNER_FACE.x2}
          y2={g.INNER_FACE.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.INNER_FACE.start} />
          <stop offset="1" stopColor={g.INNER_FACE.end} />
        </linearGradient>

        {/* Inner face vignette (vertical) */}
        <linearGradient
          id={getId('innerFaceVert')}
          x1={g.INNER_FACE_VERT.x1}
          y1={g.INNER_FACE_VERT.y1}
          x2={g.INNER_FACE_VERT.x2}
          y2={g.INNER_FACE_VERT.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.08" stopOpacity="0" />
          <stop offset="0.92" stopOpacity="0" />
          <stop offset="1" />
        </linearGradient>

        {/* Inner face vignette (horizontal) */}
        <linearGradient
          id={getId('innerFaceHoriz')}
          x1={g.INNER_FACE_HORIZ.x1}
          y1={g.INNER_FACE_HORIZ.y1}
          x2={g.INNER_FACE_HORIZ.x2}
          y2={g.INNER_FACE_HORIZ.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.08" stopOpacity="0" />
          <stop offset="0.92" stopOpacity="0" />
          <stop offset="1" />
        </linearGradient>

        {/* Ledge bottom gradient */}
        <linearGradient
          id={getId('ledgeBottom')}
          x1={g.LEDGE_BOTTOM.x1}
          y1={g.LEDGE_BOTTOM.y1}
          x2={g.LEDGE_BOTTOM.x2}
          y2={g.LEDGE_BOTTOM.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.LEDGE_BOTTOM.start} />
          <stop offset="1" stopColor={g.LEDGE_BOTTOM.end} />
        </linearGradient>

        {/* Ledge top gradient */}
        <linearGradient
          id={getId('ledgeTop')}
          x1={g.LEDGE_TOP.x1}
          y1={g.LEDGE_TOP.y1}
          x2={g.LEDGE_TOP.x2}
          y2={g.LEDGE_TOP.y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={g.LEDGE_TOP.start} />
          <stop offset="1" stopColor={g.LEDGE_TOP.end} />
        </linearGradient>

        {/* Value 6 skull body gradient (grey skull) */}
        <linearGradient
          id={getId('skullBody')}
          x1="50.73"
          y1="75.81"
          x2="152.22"
          y2="75.81"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#EDEEF0" />
          <stop offset="0.64" stopColor="#E4E5E7" />
          <stop offset="0.99" stopColor="#C7C8CA" />
        </linearGradient>

        {/* Value 6 right eye gradient (player color) */}
        <linearGradient
          id={getId('skullRightEye')}
          x1="92.7314"
          y1="87.4612"
          x2="187.887"
          y2="68.9993"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyGradient[0]} />
          <stop offset="0.24" stopColor={colorScheme.bodyGradient[1]} />
          <stop offset="0.47" stopColor={colorScheme.bodyGradient[2]} />
          <stop offset="0.75" stopColor={colorScheme.bodyGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyGradient[4]} />
        </linearGradient>

        {/* Value 6 left eye gradient (player color) */}
        <linearGradient
          id={getId('skullLeftEye')}
          x1="63.6409"
          y1="83.6773"
          x2="138.466"
          y2="69.16"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyGradient[0]} />
          <stop offset="0.24" stopColor={colorScheme.bodyGradient[1]} />
          <stop offset="0.47" stopColor={colorScheme.bodyGradient[2]} />
          <stop offset="0.75" stopColor={colorScheme.bodyGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyGradient[4]} />
        </linearGradient>

        {/* Value 6 nose connector gradient (player color) */}
        <linearGradient
          id={getId('skullNose')}
          x1="81.0166"
          y1="105.144"
          x2="105.961"
          y2="100.304"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={colorScheme.bodyGradient[0]} />
          <stop offset="0.24" stopColor={colorScheme.bodyGradient[1]} />
          <stop offset="0.47" stopColor={colorScheme.bodyGradient[2]} />
          <stop offset="0.75" stopColor={colorScheme.bodyGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyGradient[4]} />
        </linearGradient>
      </defs>

      <g clipPath={`url(#${getId('clip')})`}>
        {/* Fixed ground/background (ornate frame + inner grass face) — only when showGround */}
        {showGround && (
          <>
            {/* Drop shadow base (opacity 0.12) */}
            <g opacity="0.12">
              <path d={CRYPT_PATHS.SHADOW_BOTTOM_A} fill="black" />
              <path d={CRYPT_PATHS.SHADOW_BOTTOM_B} fill="black" />
              <path d={CRYPT_PATHS.SHADOW_OUTER_ROUND} fill="black" />
              <path d={CRYPT_PATHS.SHADOW_INNER_ROUND_A} fill="black" />
              <path d={CRYPT_PATHS.SHADOW_INNER_ROUND_B} fill="black" />
            </g>

            {/* Ledge / ground bottom */}
            <path d={CRYPT_PATHS.LEDGE_BOTTOM} fill={`url(#${getId('ledgeBottom')})`} />
            <path d={CRYPT_PATHS.LEDGE_TOP} fill={`url(#${getId('ledgeTop')})`} />

            {/* Main body — player color */}
            <path d={CRYPT_PATHS.BODY_RECT} fill={`url(#${getId('body')})`} />

            {/* Corner decorations — player color */}
            <path d={CRYPT_PATHS.CORNER_LEFT} fill={`url(#${getId('cornerLeft')})`} />
            <path d={CRYPT_PATHS.CORNER_RIGHT} fill={`url(#${getId('cornerRight')})`} />
            <path d={CRYPT_PATHS.CORNER_TOP_LEFT} fill={`url(#${getId('cornerTopLeft')})`} />

            {/* White bone/candle border decorations */}
            {CRYPT_DECORATIONS.map((path, index) => (
              <path key={index} d={path} opacity={DECORATION_OPACITY} fill="white" />
            ))}

            {/* Right frame shadows (opacity 0.55) */}
            <path opacity={SHADOW_OPACITY} d={CRYPT_PATHS.FRAME_RIGHT_OUTER} fill="black" />
            {/* Right frame gold */}
            <path d={CRYPT_PATHS.FRAME_RIGHT_OUTER} fill={`url(#${getId('frameRightGold')})`} />
            {/* Right frame dark inner */}
            <path d={CRYPT_PATHS.FRAME_RIGHT_INNER} fill={`url(#${getId('frameRightDark')})`} />

            {/* Left frame shadows (opacity 0.55) */}
            <path opacity={SHADOW_OPACITY} d={CRYPT_PATHS.FRAME_LEFT_OUTER} fill="black" />
            {/* Left frame gold */}
            <path d={CRYPT_PATHS.FRAME_LEFT_OUTER} fill={`url(#${getId('frameLeftGold')})`} />
            {/* Left frame dark inner */}
            <path d={CRYPT_PATHS.FRAME_LEFT_INNER} fill={`url(#${getId('frameLeftDark')})`} />

            {/* Top frame shadow (opacity 0.55) */}
            <path opacity={SHADOW_OPACITY} d={CRYPT_PATHS.FRAME_TOP_OUTER} fill="black" />
            {/* Top frame gold */}
            <path d={CRYPT_PATHS.FRAME_TOP_OUTER} fill={`url(#${getId('frameTopGold')})`} />
            {/* Top frame dark inner */}
            <path d={CRYPT_PATHS.FRAME_TOP_INNER} fill={`url(#${getId('frameTopDark')})`} />

            {/* Bottom frame shadow (opacity 0.55) */}
            <path opacity={SHADOW_OPACITY} d={CRYPT_PATHS.FRAME_BOTTOM_OUTER} fill="black" />
            {/* Bottom frame gold */}
            <path d={CRYPT_PATHS.FRAME_BOTTOM_OUTER} fill={`url(#${getId('frameBottomGold')})`} />
            {/* Bottom frame dark inner */}
            <path d={CRYPT_PATHS.FRAME_BOTTOM_INNER} fill={`url(#${getId('frameBottomDark')})`} />

            {/* Corner triangles */}
            <path d={CRYPT_PATHS.CORNER_TR_FILL} fill="#72531C" />
            <path d={CRYPT_PATHS.CORNER_TR_GRAD} fill={`url(#${getId('cornerTRGrad')})`} />
            <path d={CRYPT_PATHS.CORNER_TL_FILL} fill="#72531C" />
            <path d={CRYPT_PATHS.CORNER_TL_GRAD} fill={`url(#${getId('cornerTLGrad')})`} />
            <path d={CRYPT_PATHS.CORNER_BL_FILL} fill="#72531C" />
            <path d={CRYPT_PATHS.CORNER_BL_GRAD} fill={`url(#${getId('cornerBLGrad')})`} />
            <path d={CRYPT_PATHS.CORNER_BR_FILL} fill="#72531C" />
            <path d={CRYPT_PATHS.CORNER_BR_GRAD} fill={`url(#${getId('cornerBRGrad')})`} />
          </>
        )}

        {/* Dice face plate — the inner rounded-square that holds the pips.
            Part of the tumbling dice body (with the pips below), NOT the ground. */}
        {showDiceFace && (
          <>
            <path d={CRYPT_PATHS.INNER_FACE} fill={`url(#${getId('innerFace')})`} />
            <path
              d={CRYPT_PATHS.INNER_FACE_VERT_OVERLAY}
              opacity={FACE_VIGNETTE_OPACITY}
              fill={`url(#${getId('innerFaceVert')})`}
            />
            <path
              d={CRYPT_PATHS.INNER_FACE_HORIZ_OVERLAY}
              opacity={FACE_VIGNETTE_OPACITY}
              fill={`url(#${getId('innerFaceHoriz')})`}
            />
          </>
        )}

        {/* Grass texture overlay — part of the fixed ground */}
        {showGround && showGrassTexture && (
          <rect
            x={CRYPT_TEXTURE_RECT.x}
            y={CRYPT_TEXTURE_RECT.y}
            width={CRYPT_TEXTURE_RECT.width}
            height={CRYPT_TEXTURE_RECT.height}
            fill={`url(#${getId('texture')})`}
          />
        )}

        {/* Animated dice pips (skull/bone indicators) */}
        {showDiceFace && (
          <motion.g
            animate={state.combinedAnimation}
            className="dice-3d-transform"
            style={{
              transformOrigin: '93.6px 83.4px',
              opacity: isRolling ? DICE_CONFIG.INACTIVE_OPACITY : 1
            }}
            filter={`url(#${getId('dropShadow')})`}
          >
            {isValue6 ? (
              <>
                {/* Value 6: skull/head-bone design */}
                <path d={FACE_6_SKULL_BODY} fill={`url(#${getId('skullBody')})`} />
                <path d={FACE_6_RIGHT_EYE_SHADOW} fill="#660404" />
                <path d={FACE_6_RIGHT_EYE_MAIN} fill={`url(#${getId('skullRightEye')})`} />
                <path d={FACE_6_LEFT_EYE_SHADOW} fill="#660404" />
                <path d={FACE_6_LEFT_EYE_MAIN} fill={`url(#${getId('skullLeftEye')})`} />
                {/* Jaw and detail paths */}
                {indicators.map((indicator, index) => (
                  <g key={index}>
                    <path d={indicator.shadow} fill="#660404" />
                    <path
                      d={indicator.main}
                      fill={index === 3 ? `url(#${getId('skullNose')})` : 'white'}
                    />
                  </g>
                ))}
              </>
            ) : (
              /* Values 1–5: bone indicators — shadow + white pairs */
              indicators.map((indicator, index) => (
                <g key={index}>
                  <path d={indicator.shadow} fill="#3A3A3A" />
                  <path d={indicator.main} fill="white" />
                </g>
              ))
            )}
          </motion.g>
        )}
      </g>
    </svg>
  );
});

export default CryptDiceComponent;
